import { useEffect, useState } from "react";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AppRoutes from "./routes/AppRoutes";
import { ProductsProvider } from "./context/ProductsContext";
import { OfflineProvider } from "./context/OfflineContext";
import OfflineBanner from "./components/OfflineBanner";
import { KeyboardProvider } from "./context/KeyboardContext";
import VirtualKeyboard from "./components/ui/VirtualKeyboard";
import AuthKeyboardToggleFab from "./components/ui/AuthKeyboardToggleFab";
import { TRIAL_EXPIRED_EVENT } from "./services/api";
import { isPublicIsolatedPath } from "./lib/publicIsolatedPaths";

import { isShellAppAvailable, pingShellApp } from "./lib/shellApp";

function App() {
  const [trialExpiredModalOpen, setTrialExpiredModalOpen] = useState(false);
  const [trialExpiredMessage, setTrialExpiredMessage] = useState(
    "Your trial has ended. Please contact support."
  );
  const publicIsolated = isPublicIsolatedPath();

  useEffect(() => {
    // Skip ShellApp ping on public onboarding / form pages.
    if (publicIsolated) return;
    if (isShellAppAvailable()) {
      pingShellApp().catch((err) => {
        console.error("ShellApp ping failed:", err);
      });
    }
  }, [publicIsolated]);

  useEffect(() => {
    if (publicIsolated) return;

    const onTrialExpired = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      const message = customEvent.detail?.message?.trim();
      setTrialExpiredMessage(message || "Your trial has ended. Please contact support.");
      setTrialExpiredModalOpen(true);
    };

    window.addEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired as EventListener);
    return () => {
      window.removeEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired as EventListener);
    };
  }, [publicIsolated]);

  // Public business onboarding / forms: no ProductsProvider (products API),
  // no VirtualKeyboard settings fetch — only the public page tree.
  if (publicIsolated) {
    return (
      <>
        <AppRoutes />
        <ToastContainer transition={Slide} limit={3} />
      </>
    );
  }

  return (
    <OfflineProvider>
      <KeyboardProvider>
        <ProductsProvider>
          <OfflineBanner />
          <div className="min-h-screen pt-[var(--offline-banner-height,0px)]">
            <AppRoutes />
          </div>
          <ToastContainer transition={Slide} limit={3} />
          <VirtualKeyboard />
          <AuthKeyboardToggleFab />
          {trialExpiredModalOpen ? (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
              <div className="border-b px-5 py-4">
                <h3 className="text-lg font-semibold text-[#402B69]">Trial Expired</h3>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-gray-700">{trialExpiredMessage}</p>
              </div>
              <div className="flex justify-end gap-2 border-t bg-gray-50 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setTrialExpiredModalOpen(false)}
                  className="rounded-lg bg-[#885ABB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7748A4]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        ) : null}
        </ProductsProvider>
      </KeyboardProvider>
    </OfflineProvider>
  );
}

export default App;
